import { useState, useEffect, useRef, useCallback } from "react";
import { invoke } from "../lib/bridge";
import { useCableChannel } from "./useCableChannel";
import { maxId } from "../lib/collectionUtils";

interface RawChatMessage {
  id: number;
  senderMemberId: number;
  encryptedContent: string;
  createdAt: string;
}

export interface ChatMessage {
  id: number;
  senderMemberId: number;
  senderName: string;
  text: string;
  createdAt: string;
}

// Decrypts content. The sender NAME is resolved separately from the local roster
// (Phase 4b — names are no longer carried on the wire), so it's filled in by the caller.
async function decryptMessage(raw: RawChatMessage): Promise<Omit<ChatMessage, "senderName">> {
  try {
    const text = await invoke<string>("decrypt_chat_message", {
      encryptedContent: raw.encryptedContent,
    });
    return { id: raw.id, senderMemberId: raw.senderMemberId, text, createdAt: raw.createdAt };
  } catch {
    return { id: raw.id, senderMemberId: raw.senderMemberId, text: "[decryption failed]", createdAt: raw.createdAt };
  }
}

interface UseChatOptions {
  apiUrl: string;
  authToken: string | null;
  channel?: string;
  memberId?: number;
  active?: boolean;
}

const FALLBACK_POLL_MS = 30_000;

export function useChat({ apiUrl, authToken, channel = "family", active = true }: UseChatOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const lastIdRef = useRef(0);
  const fallbackRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  // Member id → decrypted name, for resolving message senders locally (Phase 4b).
  const rosterRef = useRef<Record<number, string>>({});
  const loadRoster = useCallback(async () => {
    try {
      rosterRef.current = await invoke<Record<number, string>>("get_member_names");
    } catch {
      // keep the last known roster
    }
  }, []);
  useEffect(() => { loadRoster(); }, [loadRoster]);

  // `cache` is false only when the rows came FROM the cache (avoid re-writing them).
  const appendMessages = useCallback(async (rawMessages: RawChatMessage[], cache = true) => {
    if (rawMessages.length === 0) return;
    const decrypted = await Promise.all(rawMessages.map(decryptMessage));
    // Refresh the roster once if a sender isn't known yet (e.g. a brand-new member).
    if (decrypted.some((m) => !(m.senderMemberId in rosterRef.current))) await loadRoster();
    const named = decrypted.map((m) => ({
      ...m,
      senderName: rosterRef.current[m.senderMemberId] ?? "Member",
    }));
    setMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const newOnes = named.filter((m) => !existingIds.has(m.id));
      if (newOnes.length === 0) return prev;
      return [...prev, ...newOnes].sort((a, b) => a.id - b.id);
    });
    const newMaxId = maxId(rawMessages, lastIdRef.current);
    if (newMaxId > lastIdRef.current) lastIdRef.current = newMaxId;
    // Persist live/polled messages locally so chat works offline and survives server GC.
    if (cache) invoke("cache_chat_messages", { channel, messages: rawMessages }).catch(() => {});
  }, [channel, loadRoster]);

  const poll = useCallback(async () => {
    try {
      const raw = await invoke<RawChatMessage[]>("load_chat_messages", {
        after: lastIdRef.current,
        channel,
      });
      await appendMessages(raw);
    } catch {
      // Offline or not authenticated
    }
  }, [appendMessages, channel]);

  // Initial load: render cached history first (instant + offline), then fill from server.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cached = await invoke<RawChatMessage[]>("load_cached_chat", { channel });
        if (!cancelled) await appendMessages(cached, false);
      } catch {
        // no cache yet
      }
      if (!cancelled) await poll();
    })();
    return () => { cancelled = true; };
  }, [channel, appendMessages, poll]);

  // WebSocket — primary delivery with auto-reconnect
  const wsConnectedRef = useCableChannel(
    { apiUrl, authToken },
    JSON.stringify({ channel: "FamilyChatChannel", chat_channel: channel }),
    async (message) => {
      await appendMessages([{
        id: message.id,
        senderMemberId: message.sender_member_id,
        encryptedContent: message.encrypted_content,
        createdAt: message.created_at,
      }]);
    },
  );

  // Slow fallback poll — only when WS is disconnected and tab is active
  useEffect(() => {
    clearInterval(fallbackRef.current);

    if (!active) return;

    fallbackRef.current = setInterval(() => {
      if (!wsConnectedRef.current) poll();
    }, FALLBACK_POLL_MS);

    return () => clearInterval(fallbackRef.current);
  }, [poll, active]);

  const sendMessage = useCallback(async (text: string) => {
    try {
      await invoke("send_chat_message", { message: text, channel });
      // Immediately poll to pick up our own message
      await poll();
    } catch (e) {
      console.error("Failed to send message:", e);
    }
  }, [poll, channel]);

  return { messages, sendMessage };
}
